import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useParams, Link } from "react-router-dom";
import { ArrowRight, Menu, X } from "lucide-react";
import { SITE } from "@/config";
import { SUPPORTED } from "@/i18n";
import Seo from "@/components/Seo";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import BelowFold from "@/pages/BelowFold";

const ACCENT = "#D9A441";

/* ---------------- Nav ---------------- */
const Nav = () => {
  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const links = [
    { href: "#top", label: t("nav.home") },
    { href: "#about", label: t("nav.about") },
    { href: "#work", label: t("nav.work") },
    { href: "#vision", label: t("nav.building") },
    { href: "#invest", label: "Invest", testid: "nav-invest-mobile" },
    { href: "#build", label: t("nav.build"), testid: "nav-build-mobile" },
    { href: "#contact", label: t("nav.contact") },
  ];
  return (
    <nav data-testid="main-nav" className="fixed top-0 inset-x-0 z-50 backdrop-blur-md bg-black/30 border-b hairline">
      <div className="max-w-5xl mx-auto px-5 md:px-8 h-16 flex items-center justify-between gap-4">
        <a href="#top" data-testid="nav-logo" className="text-sm font-semibold tracking-tight whitespace-nowrap">
          Anubhav Sahni
        </a>
        <div className="hidden md:flex items-center gap-8 text-sm text-neutral-400">
          <a href="#top" className="hover:text-white transition-colors duration-300">{t("nav.home")}</a>
          <a href="#about" className="hover:text-white transition-colors duration-300">{t("nav.about")}</a>
          <a href="#work" className="hover:text-white transition-colors duration-300">{t("nav.work")}</a>
          <a href="#vision" className="hover:text-white transition-colors duration-300">{t("nav.building")}</a>
          <a href="#invest" data-testid="nav-invest" className="hover:text-white transition-colors duration-300">Invest</a>
          <a href="#build" data-testid="nav-build" className="hover:text-white transition-colors duration-300">{t("nav.build")}</a>
          <a href="#contact" className="hover:text-white transition-colors duration-300">{t("nav.contact")}</a>
        </div>
        <div className="flex items-center gap-3">
          <LanguageSwitcher />
          <a
            href="#contact"
            data-testid="nav-cta"
            className="hidden sm:inline-block rounded-full bg-white text-black text-sm font-medium px-4 py-2 hover:bg-neutral-200 transition-colors duration-300 whitespace-nowrap"
          >
            {t("nav.cta")}
          </a>
          <button
            type="button"
            data-testid="nav-menu-toggle"
            aria-label="Open menu"
            onClick={() => setMenuOpen((v) => !v)}
            className="md:hidden inline-flex items-center justify-center h-9 w-9 rounded-full border hairline text-neutral-200 hover:text-white transition-colors duration-300"
          >
            {menuOpen ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            data-testid="nav-mobile-menu"
            className="md:hidden border-t hairline bg-[#0B0B0D]/95 backdrop-blur-md"
          >
            <div className="max-w-5xl mx-auto px-5 py-4 flex flex-col">
              {links.map((l) =>
                l.to ? (
                  <Link
                    key={l.label}
                    to={l.to}
                    data-testid={l.testid}
                    onClick={() => setMenuOpen(false)}
                    className="py-3 text-base text-neutral-200 border-b border-white/5 last:border-0"
                  >
                    {l.label}
                  </Link>
                ) : (
                  <a
                    key={l.label}
                    href={l.href}
                    data-testid={l.testid}
                    onClick={() => setMenuOpen(false)}
                    className="py-3 text-base text-neutral-200 border-b border-white/5"
                  >
                    {l.label}
                  </a>
                )
              )}
              <a
                href="#contact"
                onClick={() => setMenuOpen(false)}
                className="mt-4 inline-flex items-center justify-center rounded-full bg-white text-black text-sm font-medium px-5 py-3 hover:bg-neutral-200 transition-colors duration-300"
              >
                {t("nav.cta")}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

/* ---------------- Hero ---------------- */
const Hero = () => {
  const { t } = useTranslation();
  return (
    <section id="top" data-testid="hero-section" className="relative min-h-screen flex items-center">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[520px] h-[260px] rounded-full blur-[140px] opacity-[0.18]" style={{ background: ACCENT }} />
      <div className="relative max-w-5xl mx-auto px-5 md:px-8 w-full pt-24">
        <motion.p
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }}
          className="text-xs uppercase tracking-[0.25em] text-neutral-500 mb-8"
        >
          {t("hero.eyebrow")}
        </motion.p>

        <motion.h1
          initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="text-4xl sm:text-6xl lg:text-7xl font-bold tracking-[-0.03em] leading-[1.05] max-w-3xl"
        >
          {t("hero.t1")}<br />
          {t("hero.t2")}<br />
          <span className="text-neutral-500">{t("hero.t3")}</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.7, delay: 0.25 }}
          className="mt-8 text-lg md:text-xl text-neutral-400 leading-relaxed max-w-xl"
        >
          {t("hero.p")}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.4 }}
          className="mt-10 flex items-center gap-4"
        >
          <a
            href="#contact"
            data-testid="hero-cta"
            className="group flex items-center gap-2 rounded-full bg-white text-black text-sm font-medium px-6 py-3.5 hover:bg-neutral-200 transition-colors duration-300"
          >
            {t("hero.cta")}
            <ArrowRight size={17} className="group-hover:translate-x-0.5 transition-transform duration-300 rtl:rotate-180" />
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default function Landing() {
  const { lang } = useParams();
  const { i18n } = useTranslation();

  useEffect(() => {
    if (lang && SUPPORTED.includes(lang) && lang !== i18n.resolvedLanguage) {
      i18n.changeLanguage(lang);
    }
  }, [lang, i18n]);

  return (
    <main className="bg-[#08080A] text-[#EDEDED]">
      <Seo />
      <Nav />
      <Hero />
      <BelowFold />
    </main>
  );
}
